import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { ChevronLeft, Headset, LogOut, ShieldCheck, User } from "lucide-react";
import { ghostButtonClass, Status } from "../components/ui";
import { useAppOutlet } from "../components/Shell";
import { landingPathFor } from "../lib/landing";
import { SyncCard } from "../profile/SyncCard";
import { useProfile } from "../profile/useProfile";
import { initialsOf } from "../lib/datetime";
import { APP_VERSION } from "../version";
import type { Session } from "../types";

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <dt className="shrink-0 text-sm text-app-muted">{label}</dt>
      <dd className="min-w-0 text-end text-sm font-medium text-app-ink">{value}</dd>
    </div>
  );
}

export function Profile({ session }: { session: Session }) {
  const { t } = useTranslation();
  const { requestLogout } = useAppOutlet();
  const { me, loading, error } = useProfile();

  const teacher = session.role === "teacher";
  const name = me?.name || session.assignedID;
  const assignment = [me?.center, me?.institute].filter(Boolean);
  // Teachers see less: no role badge, grade/subject, admin note or version.
  const teaching = teacher
    ? []
    : [me?.grade && t("gradeLabel", { grade: me.grade }), me?.subject].filter(Boolean);

  return (
    <div className="flex flex-col gap-5 pb-6 pt-2">
      {session.role !== "teacher" && (
        <NavLink to={landingPathFor(session.role)} className={ghostButtonClass}>
          <ChevronLeft size={16} />
          {t("backToHome")}
        </NavLink>
      )}
      <section className="flex items-center gap-4 rounded-xl bg-surface p-4 shadow-raised">
        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-accent-brand text-lg font-semibold text-content-on-accent">
          {me ? initialsOf(me.name, session.assignedID) : <User size={26} />}
        </span>
        <span className="flex min-w-0 flex-col">
          <strong className="truncate text-base font-semibold text-app-ink">{name}</strong>
          <span className="mt-0.5 truncate text-sm text-app-muted">
            {teacher ? `${t("teacherIDLabel")}: ` : ""}
            <span dir="ltr">{session.assignedID}</span>
          </span>
          {!teacher && (
            <span className="mt-1.5 inline-flex w-fit items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-accent">
              <ShieldCheck size={12} />
              {t("navFacilitator")}
            </span>
          )}
        </span>
      </section>

      {loading && <Status variant="loading" title={t("loading")} message={t("loadingProfile")} />}
      {error && <Status variant="error" title={t("profileUnavailable")} message={error} />}

      {me && (assignment.length > 0 || teaching.length > 0) && (
        <section className="flex flex-col rounded-xl bg-surface p-4 shadow-raised">
          <h3 className="mb-1 text-sm font-semibold text-app-ink">{t("assignment")}</h3>
          <dl className="divide-y divide-app-line">
            {me.center && <Row label={t("centerAssigned")} value={me.center} />}
            {me.institute && <Row label={t("institute")} value={me.institute} />}
            {teaching.length > 0 && <Row label={t("teaches")} value={teaching.join(" · ")} />}
          </dl>
          {!teacher && (
            <p className="pt-2 text-xs leading-5 text-app-muted">{t("assignmentManagedByAdmin")}</p>
          )}
        </section>
      )}

      <SyncCard canClear={!teacher} />

      <section className="flex flex-col rounded-xl bg-surface p-4 shadow-raised">
        <h3 className="mb-1 text-sm font-semibold text-app-ink">{t("support")}</h3>
        <NavLink
          to="/helpline"
          className="flex items-center gap-2 py-2 text-sm font-medium text-accent"
        >
          <Headset size={16} />
          {t("navHelpline")}
        </NavLink>
        {!teacher && (
          <dl className="divide-y divide-app-line">
            <Row label={t("appVersion")} value={APP_VERSION} />
          </dl>
        )}
        <p className="pt-2 text-xs leading-5 text-app-muted">
          {t(teacher ? "passwordViaHelpline" : "passwordViaAdmin")}
        </p>
      </section>

      <button
        type="button"
        className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-surface text-base font-semibold text-accent-strong shadow-raised transition-colors active:bg-danger-bg"
        onClick={requestLogout}
      >
        <LogOut size={18} />
        {t("signOut")}
      </button>
    </div>
  );
}
